import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Loading from '../components/Loading';
import './Profile.css';

function Profile() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState({
    fname: '',
    lname: '',
    email: '',
    phone: '',
  });
  const [prefs, setPrefs] = useState({
    disabled: false,
    send_new_submission: false,
    new_club_members: false,
    send_new_rule: false,
    send_stats: false,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
        return;
      }
      // Load user data
      loadUserData();
    }
  }, [user, authLoading, navigate]);

  const loadUserData = async () => {
    try {
      setLoadingUser(true);
      const [meRes, prefsRes] = await Promise.all([
        authApi.getMe(),
        authApi.getProfilePrefs().catch(() => ({ data: { success: false } })),
      ]);
      if (meRes.data.success) {
        const userData = meRes.data.data;
        setFormData({
          fname: userData.fname || '',
          lname: userData.lname || '',
          email: userData.email || '',
          phone: userData.phone || '',
        });
      } else {
        setError(meRes.data.message || 'Failed to load profile data');
      }
      if (prefsRes.data?.success && prefsRes.data.data) {
        setPrefs({
          disabled: !!prefsRes.data.data.disabled,
          send_new_submission: !!prefsRes.data.data.send_new_submission,
          new_club_members: !!prefsRes.data.data.new_club_members,
          send_new_rule: !!prefsRes.data.data.send_new_rule,
          send_stats: !!prefsRes.data.data.send_stats,
        });
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'Failed to load profile data. ' + (err.message || '')
      );
    } finally {
      setLoadingUser(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
    setSuccess('');
  };

  const handlePrefChange = (e) => {
    const { name, checked } = e.target;
    setPrefs((prev) => ({ ...prev, [name]: checked }));
    setError('');
    setSuccess('');
  };

  const handleSavePrefs = async (e) => {
    e.preventDefault();
    setSavingPrefs(true);
    setError('');
    setSuccess('');
    try {
      const response = await authApi.updateProfilePrefs(prefs);
      if (response.data.success) {
        setSuccess('Alert preferences saved.');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save alert preferences.');
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Note: You'll need to create an update endpoint in your API
      // For now, this is a placeholder
      setSuccess('Profile update functionality coming soon. API endpoint needed.');
      // const response = await authApi.updateProfile(formData);
      // if (response.data.success) {
      //   setSuccess('Profile updated successfully');
      // }
    } catch (err) {
      setError(
        err.response?.data?.message || 
        'Failed to update profile. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loadingUser) {
    return (
      <>
        <Header />
        <div className="container">
          <Loading />
        </div>
        <Footer />
      </>
    );
  }

  if (!user) {
    return null; // Will redirect
  }

  return (
    <>
      <Header />
      <div className="container">
        <div className="profile-page">
          <h1>Profile</h1>
          
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          
          <div className="profile-info">
            <div className="info-item">
              <strong>User ID:</strong> {user.id || user.guy_id}
            </div>
            {user.profile && (
              <div className="info-item">
                <strong>Profile:</strong> {user.profile}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="fname">First Name</label>
              <input
                type="text"
                id="fname"
                name="fname"
                value={formData.fname}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="lname">Last Name</label>
              <input
                type="text"
                id="lname"
                name="lname"
                value={formData.lname}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Profile'}
            </button>
          </form>

          {user.profile === 'admin' && (
            <section className="profile-prefs" aria-labelledby="alert-prefs-heading">
              <div className="profile-prefs-card">
                <h2 id="alert-prefs-heading">Alert Preferences</h2>
                <form onSubmit={handleSavePrefs} className="prefs-form">
                  <label className="prefs-label prefs-label-disabled">
                    <input
                      type="checkbox"
                      name="disabled"
                      checked={prefs.disabled}
                      onChange={handlePrefChange}
                      disabled={savingPrefs}
                      className="prefs-checkbox"
                    />
                    <span>Disabled</span>
                  </label>
                  <ul className={`prefs-list ${prefs.disabled ? 'prefs-list-greyed' : ''}`}>
                    <li>
                      <label className="prefs-label">
                        <input
                          type="checkbox"
                          name="send_new_submission"
                          checked={prefs.send_new_submission}
                          onChange={handlePrefChange}
                          disabled={savingPrefs || prefs.disabled}
                          className="prefs-checkbox"
                        />
                        <span>Send New Submissions</span>
                      </label>
                    </li>
                    <li>
                      <label className="prefs-label">
                        <input
                          type="checkbox"
                          name="new_club_members"
                          checked={prefs.new_club_members}
                          onChange={handlePrefChange}
                          disabled={savingPrefs || prefs.disabled}
                          className="prefs-checkbox"
                        />
                        <span>Send New Club Members</span>
                      </label>
                    </li>
                    <li>
                      <label className="prefs-label">
                        <input
                          type="checkbox"
                          name="send_new_rule"
                          checked={prefs.send_new_rule}
                          onChange={handlePrefChange}
                          disabled={savingPrefs || prefs.disabled}
                          className="prefs-checkbox"
                        />
                        <span>Send New GuyRules</span>
                      </label>
                    </li>
                    <li>
                      <label className="prefs-label">
                        <input
                          type="checkbox"
                          name="send_stats"
                          checked={prefs.send_stats}
                          onChange={handlePrefChange}
                          disabled={savingPrefs || prefs.disabled}
                          className="prefs-checkbox"
                        />
                        <span>Send Stats</span>
                      </label>
                    </li>
                  </ul>
                  <button type="submit" className="btn btn-primary" disabled={savingPrefs}>
                    {savingPrefs ? 'Saving...' : 'Save Preferences'}
                  </button>
                </form>
              </div>
            </section>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

export default Profile;
